/**
 * El fichero del croquis de UN borrador, por nombre (spec 2026-11-02
 * wizard-draft-ownership § 6, «Fotos y croquis entran en el ámbito»; revisión
 * de la Task 7).
 *
 * Los asistentes guardan el croquis dibujado como un fichero en una carpeta de
 * OPFS (`canvas-accident`, `canvas-action`) que es de todo el origen: la
 * comparten las pestañas y los asistentes de crear y editar. Las altas subían
 * «el primer fichero de la carpeta» (`UploadImage(undefined, CANVAS)`), fuera
 * suyo o no: el croquis de una edición abandonada, el de otro alta o el de otra
 * pestaña acababa en el accidente (o la actuación) nuevo.
 *
 * Desde ahora el borrador dice si tiene croquis y con qué NOMBRE se guardó su
 * fichero (`fileName`, lo apunta `handleAddSketch` al dibujarlo con
 * `saveDraftSketchFile`). Las altas, validar actuación y editar accidente
 * suben solo ese fichero, y solo si su borrador tiene croquis (Ruling 15).
 *
 * 🔴 La marca `fileName` distingue tres casos (revisión de la Task 9):
 * - `fileName: "<nombre>"` → borrador nuevo con fichero propio: ese.
 * - `fileName: null` → borrador nuevo SIN fichero propio (el lienzo dio un
 *   blob vacío y no se guardó nada): no se sube ninguno. Sin la marca caía en
 *   «el primero de la carpeta», que podía ser el croquis de otro.
 * - sin `fileName` → borrador de ANTES de este cambio. Solo si es un dibujo
 *   local (su `id` es el data URI del lienzo) se cae al primero, lo de
 *   siempre; un croquis que vino del servidor no tiene fichero que subir.
 */

import type { FormImageSource } from '../offline/formStorage';
import { makeUniqueFileName } from './uniqueFileName';

/** El croquis de un borrador: si lo hay, el nombre de su fichero y si es de antes de la marca. */
export type DraftSketchFile = {
  hasSketch: boolean;
  /** El fichero propio; `null` si no tiene (o el borrador es de antes). */
  fileName: string | null;
  /** Dibujo local de un borrador de antes de la marca: puede caer al primero. */
  legacy: boolean;
};

const NO_SKETCH: DraftSketchFile = { hasSketch: false, fileName: null, legacy: false };

/**
 * Lee el croquis de un borrador: el valor del campo (`sketch` de la copia del
 * paso 2 del accidente, `editionSketch` de «Datos Act»), que es el JSON de un
 * `Sketch` o el objeto ya parseado. Nunca lanza.
 */
export const draftSketchFile = (sketchField: unknown): DraftSketchFile => {
  let sketch: unknown = sketchField;
  if (typeof sketch === 'string') {
    try {
      sketch = JSON.parse(sketch);
    } catch {
      return NO_SKETCH;
    }
  }
  if (!sketch || typeof sketch !== 'object' || Array.isArray(sketch)) return NO_SKETCH;
  const { fileName, id } = sketch as { fileName?: unknown; id?: unknown };
  const hasMarker = Object.prototype.hasOwnProperty.call(sketch, 'fileName');
  return {
    hasSketch: true,
    fileName: typeof fileName === 'string' && fileName.length > 0 ? fileName : null,
    legacy: !hasMarker && typeof id === 'string' && id.startsWith('data:'),
  };
};

/**
 * La marca `fileName` de un croquis guardado en el borrador, para conservarla
 * al volver al paso: el nombre, `null` (sin fichero propio) o nada (borrador
 * de antes de la marca). Si se perdiera, el efecto que reescribe el campo
 * dejaría el borrador sin marca y el alta lo trataría como de antes.
 */
export const sketchFileNameMarker = (
  parsedSketch: unknown
): { fileName?: string | null } => {
  if (
    !parsedSketch ||
    typeof parsedSketch !== 'object' ||
    !Object.prototype.hasOwnProperty.call(parsedSketch, 'fileName')
  ) {
    return {};
  }
  const { fileName } = parsedSketch as { fileName?: unknown };
  return { fileName: typeof fileName === 'string' && fileName.length > 0 ? fileName : null };
};

/**
 * El fichero de croquis de un borrador dentro de la carpeta: el de su nombre.
 *
 * Sin nombre (borrador de antes de este cambio: una operación ya encolada en la
 * tablet, o una sesión abierta al desplegar) se cae al comportamiento anterior,
 * el PRIMERO de la carpeta, para no dejar sin croquis lo que ya estaba en
 * camino (`.claude/rules/guard-nuevo-cola-offline.md`). Con nombre y sin ese
 * fichero (otra pestaña vació la carpeta al dibujar el suyo), `undefined`:
 * mejor sin croquis que con el de otro.
 */
export const pickSketchFile = <T extends { file: File }>(
  files: readonly T[],
  fileName: string | null | undefined
): T | undefined => (fileName ? files.find((entry) => entry.file.name === fileName) : files[0]);

/**
 * Nombre del fichero de croquis que se lleva un guardado de este borrador (el
 * que sube `UploadImage` con `canvasFileName`, el que copia el rescate sin red
 * y el que captura la cola): `null` si el borrador no tiene croquis; si no, el
 * suyo — o, SOLO en un dibujo local de antes de la marca, el primero de la
 * carpeta (mismo criterio que `pickSketchFile`). Nunca lanza.
 */
export const draftSketchFileName = async (
  sketch: DraftSketchFile,
  images: { list(directory: string): Promise<Array<{ file: File }>> },
  directory: string
): Promise<string | null> => {
  if (!sketch.hasSketch) return null;
  if (sketch.fileName) return sketch.fileName;
  // Borrador nuevo sin fichero propio, o croquis del servidor: nada que subir.
  if (!sketch.legacy) return null;
  try {
    return pickSketchFile(await images.list(directory), null)?.file?.name ?? null;
  } catch {
    return null;
  }
};

/**
 * Guarda en `directory` el fichero del croquis RECIÉN dibujado de un borrador y
 * devuelve con qué nombre quedó: es el que el borrador apunta en `fileName`
 * (Ruling 15 de la ola final; spec 2026-11-02 § 6, «el guardado sube solo los
 * ficheros que lista el borrador, nunca lo que haya en la carpeta»).
 *
 * - Borra SOLO el fichero propio anterior (`previousFileName`), por nombre.
 *   Antes se vaciaba la carpeta ENTERA: entre pestañas, dibujar en una tiraba
 *   el croquis sin guardar de la otra, y validar/editar subían «el primero»,
 *   que podía ser el de otra pestaña.
 * - Nombre LIBRE: `generateFileName` sella la hora al segundo y la carpeta la
 *   comparten las pestañas y los asistentes de crear y editar; guardar en OPFS
 *   no pisa un fichero que existe (se quedaría el contenido del otro). El
 *   anterior propio cuenta como ocupado, así que el nuevo nunca se llama igual.
 *   Si NO se pudo listar la carpeta, no se da por vacía: el nombre lleva un
 *   sufijo aleatorio para que no pueda coincidir con el de otro borrador
 *   (Ruling 20).
 *
 * 🔴 Que devuelva un nombre NO garantiza que el fichero esté escrito: la
 * escritura de la pantalla (`saveFilesIntoADirectory`) se traga sus errores
 * (Sentry + log) y no sobrescribe un fichero que ya exista. Si falló, el
 * borrador apunta a un nombre sin fichero y el guardado no sube ninguno — avisa
 * `sketch-missing:<entidad>` (`UploadImage`), nunca sube el de otro. Solo lanza
 * lo que lance la fuente de imágenes que se le pase.
 */
export const saveDraftSketchFile = async (
  images: Pick<FormImageSource, 'list' | 'save' | 'remove'>,
  directory: string,
  file: File,
  previousFileName: string | null | undefined
): Promise<string> => {
  const taken = new Set<string>();
  let listed = true;
  try {
    (await images.list(directory)).forEach((entry) => taken.add(entry.file.name));
  } catch {
    // Carpeta aún sin crear o ilegible: no se sabe qué nombres hay ocupados.
    listed = false;
  }
  if (previousFileName) taken.add(previousFileName);
  const fileName = listed
    ? makeUniqueFileName(file.name, taken)
    : withRandomSuffix(file.name);
  await removeDraftSketchFile(images, directory, previousFileName);
  await images.save(directory, [
    fileName === file.name
      ? file
      : new File([file], fileName, { type: file.type, lastModified: file.lastModified }),
  ]);
  return fileName;
};

/** `nombre` → `nombre_<aleatorio>` (delante de la extensión si la hay). */
const withRandomSuffix = (name: string): string => {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)}_${random}${name.slice(dot)}` : `${name}_${random}`;
};

/**
 * Quita el fichero del croquis de UN borrador, por nombre; nunca la carpeta
 * (los demás ficheros son de otros borradores). Sin nombre (borrador de antes
 * de la marca, o sin fichero propio) no toca nada: lo que quede lo recoge la
 * limpieza del ciclo de vida (Ruling 16). Nunca lanza.
 */
export const removeDraftSketchFile = async (
  images: Pick<FormImageSource, 'remove'>,
  directory: string,
  fileName: string | null | undefined
): Promise<void> => {
  if (!fileName) return;
  try {
    await images.remove(directory, fileName);
  } catch {
    // Ya no estaba o no se pudo borrar: queda huérfano, nadie lo lista.
  }
};
